import React, { useEffect, useMemo, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';

import StaticAdsService from 'services/StaticAdsService';

import { buildFinalizeItems, buildSeedNames, FinalizeSection } from './staticGroups';
import { FinalizeResult, GenerationRow, GroupInfo } from './types';

// Finalize = publish: each selected output goes to its product's Dropbox
// folder and gets a row in the Notion creatives table. Names are editable
// per output; results render per-step so partial success (uploaded but no
// Notion row) is visible, never silent.
//
// A selection that includes a stack asks one extra question: publish the stack
// as ONE creative with numbered files and a single Notion row (batch, the
// default — that is what a stack is for), or as separate creatives that merely
// share their copy (individual). Ungrouped rows are untouched by any of this.

interface FinalizeDialogProps {
  open: boolean;
  /**
   * Closing the dialog. `published` is true only when a publish actually ran,
   * so the caller clears its selection only then: a cancelled dialog — or a
   * batch the server REJECTED without publishing anything — must keep the
   * selection intact so it can be edited and retried.
   */
  onClose: (outcome: { published: boolean }) => void;
  rows: GenerationRow[];
  /** The stacks behind those rows — a stack publishes under its own name. */
  groups: Record<string, GroupInfo>;
  service: StaticAdsService;
  /** Called after a run completes (any outcome) so the list can refresh. */
  onFinished: () => void;
}

/** The Dropbox + Notion outcome of one published item — identical for a loose
 * creative and a batched stack, since both are one Dropbox folder and one
 * Notion row. Carries the ledger outcome and any warnings too, so every place
 * a result is shown says the same full truth about it. */
function StepAlerts({ result }: { result: FinalizeResult }) {
  if (result.error) return <Alert severity="error">{result.error}</Alert>;
  return (
    <>
      {result.dropbox?.ok ? (
        <Alert severity="success">
          Uploaded to {result.dropbox.path}
          {result.dropbox.url && <> — <Link href={result.dropbox.url} target="_blank" rel="noopener">open folder</Link></>}
        </Alert>
      ) : (
        <Alert severity="error">Dropbox: {result.dropbox?.error || 'failed'}</Alert>
      )}
      {result.notion?.ok ? (
        <Alert severity={result.notion.deduped ? 'info' : 'success'}>
          {result.notion.deduped ? 'Already in Notion under this name — row not duplicated' : 'Notion row added'}
          {result.notion.pageUrl && <> — <Link href={result.notion.pageUrl} target="_blank" rel="noopener">open</Link></>}
          {!!result.notion.missing?.length && ` (table has no ${result.notion.missing.join(', ')} column)`}
        </Alert>
      ) : (
        <Alert severity="warning">Notion: {result.notion?.error || 'failed'} — the Dropbox upload still happened.</Alert>
      )}
      {/* The publish HAPPENED but was not written to the delivery ledger. That
          missing row is what makes the next attempt under the same name report
          a conflict with "a different creative", so it must never be silent. */}
      {result.ledger && !result.ledger.ok && (
        <Alert severity="warning">
          Publish record not saved: {result.ledger.error || 'unknown error'} — the files ARE in
          Dropbox, but re-publishing under the same name may report a name conflict. Do NOT
          re-publish this; ask an engineer to check the delivery ledger.
        </Alert>
      )}
      {/* The rename is bookkeeping attached to the publish — it failing must
          not read as the publish failing. */}
      {result.nameSaved && !result.nameSaved.ok && (
        <Alert severity="warning">
          The publish went through, but the new name was not saved:{' '}
          {result.nameSaved.error || 'unknown error'} — the creative keeps its old name.
        </Alert>
      )}
      {result.warnings?.map(warning => (
        <Alert key={warning} severity="warning">{warning}</Alert>
      ))}
    </>
  );
}

export default function FinalizeDialog({ open, onClose, rows, groups, service, onFinished }: FinalizeDialogProps) {
  const [names, setNames] = useState<Record<string, string>>({});
  // groupId → how to publish that stack. Absent means the 'batch' default.
  const [modes, setModes] = useState<Record<string, 'batch' | 'individual'>>({});
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<FinalizeResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  // The selection AS IT WAS WHEN THE DIALOG OPENED. The parent rebuilds `rows`
  // into a new array on every one of its renders, so anything derived from the
  // prop was reset by every parent render: the results panel was wiped the
  // instant the parent cleared its selection (a failed Notion row looked
  // exactly like a clean publish), and a stack switched to 'Separate' silently
  // snapped back to 'One creative' — an irreversible change of publish shape.
  // The dialog owns its selection for the life of one open instead.
  const [snapshot, setSnapshot] = useState<GenerationRow[]>([]);
  // The names the dialog opened with, kept apart from `names` (which the user
  // edits) so a blanked-out field falls back to its NUMBERED default — falling
  // back to the unnumbered one collided with a sibling in the same submit.
  const [defaultNames, setDefaultNames] = useState<Record<string, string>>({});
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  // Same reasoning as `rows`: the parent rebuilds this object every render, so
  // derived state must not follow it.
  const groupsRef = useRef(groups);
  groupsRef.current = groups;

  // Split the selection: everything ungrouped in one bucket, then one section
  // per stack in the order its first member was selected.
  const { loose, groupIds, membersOf } = useMemo(() => {
    const looseRows: GenerationRow[] = [];
    const order: string[] = [];
    const byGroup = new Map<string, GenerationRow[]>();
    for (const row of snapshot) {
      const groupId = row.groupId;
      if (!groupId) { looseRows.push(row); continue; }
      const existing = byGroup.get(groupId);
      if (existing) existing.push(row);
      else { byGroup.set(groupId, [row]); order.push(groupId); }
    }
    return { loose: looseRows, groupIds: order, membersOf: byGroup };
  }, [snapshot]);

  const sections: FinalizeSection[] = useMemo(() => {
    const out: FinalizeSection[] = [];
    if (loose.length) out.push({ kind: 'loose', rows: loose });
    for (const groupId of groupIds) {
      out.push({ kind: 'group', groupId, mode: modes[groupId] || 'batch', rows: membersOf.get(groupId) || [] });
    }
    return out;
  }, [loose, groupIds, membersOf, modes]);

  // Runs on OPEN only — `rows` is read through a ref so a parent re-render
  // cannot re-trigger this reset (see the snapshot note above).
  useEffect(() => {
    if (!open) return;
    const captured = rowsRef.current;
    // Deduplicate within the selection per product+size — repeats get #2, #3….
    // Stacks and loose rows share ONE counter: numbering them separately gave
    // a stack and a loose row of the same product+size the same name, which
    // the Notion dedupe then swallowed. See buildDefaultNames.
    // A creative that has been NAMED seeds under its own name; everything else
    // keeps the derived default.
    const base = buildSeedNames(captured, groupsRef.current);
    setSnapshot(captured);
    setDefaultNames(base);
    setNames(base);
    setModes({});
    setResults(null);
    setError(null);
  }, [open]);

  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      // A blanked-out field falls back to what it was SEEDED with, not to the
      // derived default: publishing "Pillow — 2026-08-30 — 1X1" while the
      // creative is called "Hero" is the wrong answer.
      const effective: Record<string, string> = {};
      for (const row of snapshot) {
        effective[row.generationId] = (names[row.generationId] || '').trim() || defaultNames[row.generationId];
      }
      for (const groupId of groupIds) {
        effective[groupId] = (names[groupId] || '').trim() || defaultNames[groupId];
      }
      const resp = await service.batchFinalize(buildFinalizeItems(sections, effective, defaultNames));
      setResults(resp.results);
      onFinished();
    } catch (err: any) {
      // The server rejects an oversized batch wholesale — nothing was
      // published — and its message names the limit and what to do about it,
      // so it is shown verbatim and the selection is left untouched.
      setError(err?.response?.data?.error || err?.message || 'Finalize failed');
    } finally {
      setBusy(false);
    }
  };

  const close = () => onClose({ published: !!results });

  // Group results are keyed by groupId; plain ones by generationId. Matching a
  // plain row against a group result would attribute the stack's outcome to
  // one arbitrary member, so the two lookups stay disjoint.
  const resultFor = (id: string) => results?.find(r => !r.groupId && r.generationId === id);
  const groupResultFor = (groupId: string) => results?.find(r => r.groupId === groupId);

  const nameField = (row: GenerationRow) => (
    <TextField
      size="small" fullWidth label="Name" disabled={busy || !!results}
      value={names[row.generationId] || ''}
      onChange={e => setNames(prev => ({ ...prev, [row.generationId]: e.target.value }))}
      helperText={row.productName ? `Product: ${row.productName}` : undefined}
    />
  );

  const looseRow = (row: GenerationRow) => {
    const result = resultFor(row.generationId);
    return (
      <Box key={row.generationId} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
          {row.outputImageUrl && (
            <Box component="img" src={row.outputImageUrl} alt=""
              sx={{ width: 64, height: 64, objectFit: 'cover', borderRadius: 1 }} />
          )}
          <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
            {nameField(row)}
            {result && <StepAlerts result={result} />}
          </Stack>
        </Stack>
      </Box>
    );
  };

  const groupSection = (section: Extract<FinalizeSection, { kind: 'group' }>) => {
    const members = section.rows;
    const first = members[0];
    const result = groupResultFor(section.groupId);
    const batch = section.mode === 'batch';
    const groupName = names[section.groupId] || '';

    return (
      <Box key={section.groupId} sx={{ border: '1px solid', borderColor: 'primary.light', borderRadius: 1, p: 1.25 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
          <Typography variant="subtitle2" sx={{ flex: 1, minWidth: 160 }}>
            Group of {members.length} — {first?.productName || 'no product'}
          </Typography>
          <ToggleButtonGroup
            size="small" exclusive value={section.mode} disabled={busy || !!results}
            onChange={(_e, next) => next && setModes(prev => ({ ...prev, [section.groupId]: next }))}
          >
            <ToggleButton value="batch">One creative</ToggleButton>
            <ToggleButton value="individual">Separate</ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        <Stack direction="row" spacing={0.75} sx={{ mb: 1, flexWrap: 'wrap', gap: 0.75 }}>
          {members.map(row => (row.outputImageUrl ? (
            <Box key={row.generationId} component="img" src={row.outputImageUrl} alt=""
              sx={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 1 }} />
          ) : null))}
        </Stack>

        {batch ? (
          <Stack spacing={0.75}>
            <TextField
              size="small" fullWidth label="Name" disabled={busy || !!results}
              value={groupName}
              onChange={e => setNames(prev => ({ ...prev, [section.groupId]: e.target.value }))}
              helperText={first?.productName ? `Product: ${first.productName}` : undefined}
            />
            <Typography variant="caption" color="text.secondary">
              Uploads {groupName || 'the name above'}-1…-{members.length}; one Notion row with the shared copy.
            </Typography>
          </Stack>
        ) : (
          <Stack spacing={0.75}>
            <Typography variant="caption" color="text.secondary">
              Each publishes separately with the group&apos;s shared copy.
            </Typography>
            {members.map(row => (
              <Stack key={row.generationId} spacing={0.5}>
                {nameField(row)}
                {(() => { const r = resultFor(row.generationId); return r ? <StepAlerts result={r} /> : null; })()}
              </Stack>
            ))}
          </Stack>
        )}

        {result && (
          <Stack spacing={0.75} sx={{ mt: 1 }}>
            <StepAlerts result={result} />
            {!!result.files?.length && (
              <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                <Typography variant="overline" color="text.secondary">
                  Files ({result.files.length})
                </Typography>
                <Stack spacing={0.25}>
                  {result.files.map(file => (
                    <Typography
                      key={file.generationId}
                      variant="caption"
                      color={file.error ? 'error.main' : file.skipped ? 'text.disabled' : 'text.secondary'}
                    >
                      {file.error ? '✕ ' : file.skipped ? '– ' : '✓ '}
                      {file.url
                        ? <Link href={file.url} target="_blank" rel="noopener">{file.path}</Link>
                        : file.path}
                      {file.error ? ` — ${file.error}` : file.skipped ? ' — skipped' : ''}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            )}
            {/* Members the server refused to publish. Surfaced as a warning,
                never folded into the success line — a stack that shipped 3 of 5
                must not read as fully published. */}
            {!!result.skippedMembers?.length && (
              <Alert severity="warning">
                {result.skippedMembers.length} creative{result.skippedMembers.length === 1 ? '' : 's'} not published:
                <Box component="ul" sx={{ m: 0.5, pl: 2.5 }}>
                  {result.skippedMembers.map(m => (
                    <li key={m.generationId}><Typography variant="caption">{m.reason}</Typography></li>
                  ))}
                </Box>
              </Alert>
            )}
          </Stack>
        )}
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : close} maxWidth="md" fullWidth>
      <DialogTitle>Finalize {snapshot.length} creative{snapshot.length === 1 ? '' : 's'}</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Each creative uploads to its product&apos;s Dropbox folder and gets a row in the Notion
          creatives table. Folders and the Notion page are set on the Publishing tab.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}
        {!snapshot.length && (
          <Alert severity="info">Nothing selected — tick the checkbox on a DONE creative first.</Alert>
        )}
        <Stack spacing={1.5}>
          {sections.map(section => (section.kind === 'loose'
            ? <React.Fragment key="loose">{section.rows.map(row => looseRow(row))}</React.Fragment>
            : groupSection(section)))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={close} disabled={busy}>{results ? 'Close' : 'Cancel'}</Button>
        {!results && (
          <Button
            variant="contained" onClick={run} disabled={busy || !snapshot.length}
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : undefined}
          >
            {busy ? 'Publishing…' : 'Finalize'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}
